/* Native Shopify URLs only; do not simulate products, counts or OR/AND matching.
 * Updated 2026-09-03 / kaho + Codex. */
(function (scope) {
  const isNative = name => name.startsWith('filter.');
  function buildUrl(current, {mode, entries = [], represented = [], sort, anchor = '', productSearch = false}) {
    const url = new URL(current);
    if (productSearch) url.searchParams.set('type', 'product');
    // Old demo state must never become an applied native condition.
    [...url.searchParams.keys()].filter(name => name.startsWith('fdp_')).forEach(name => url.searchParams.delete(name));
    if (mode === 'clear') {
      [...url.searchParams.keys()].filter(isNative).forEach(name => url.searchParams.delete(name));
    } else if (mode === 'apply') {
      for (const [name, value] of entries) {
        if (['q', 'type', 'sort_by', 'view'].includes(name) && !url.searchParams.has(name)) url.searchParams.set(name, value);
      }
      [...new Set(represented)].filter(isNative).forEach(name => url.searchParams.delete(name));
      const seen = new Set();
      for (const [name, value] of entries) {
        const identity = JSON.stringify([name, value]);
        if (isNative(name) && represented.includes(name) && !seen.has(identity)) {
          url.searchParams.append(name, value);
          seen.add(identity);
        }
      }
    } else if (mode === 'sort') {
      url.searchParams.set('sort_by', sort);
    }
    url.searchParams.delete('page');
    url.hash = anchor;
    return url.toString();
  }

  // Preserve native controls as the source of submission state. A merged
  // control has no name: mounting it or applying untouched partial selections
  // must not silently add the other STANDARD/LARGE value.
  function enhanceItemChoices(form) {
    for (const group of form.querySelectorAll('[data-fdp-item-group]')) {
      if (group.dataset.fdpItemState !== 'mergeable' || group.dataset.fdpItemEnhanced) continue;
      const inputs = [...group.querySelectorAll('input[type="checkbox"][name]')];
      const names = new Set(inputs.map(input => input.name));
      const values = new Set(inputs.map(input => input.value));
      if (inputs.length < 2 || names.size !== 1 || values.size !== inputs.length || !isNative(inputs[0].name)) {
        group.dataset.fdpItemState = 'ambiguous_native_values';
        continue;
      }
      const label = form.ownerDocument.createElement('label');
      label.className = 'fdp-search-filter-choice';
      label.dataset.fdpNativeChoice = '';
      label.dataset.fdpMergedChoice = '';
      const control = form.ownerDocument.createElement('input');
      control.type = 'checkbox';
      control.dataset.fdpMergedControl = '';
      const title = form.ownerDocument.createElement('span');
      title.textContent = group.dataset.fdpItemLabel;
      label.append(control, title);
      const refresh = () => {
        const selected = inputs.filter(input => input.checked).length;
        control.checked = selected === inputs.length;
        control.indeterminate = selected > 0 && selected < inputs.length;
        label.classList.toggle('is-selected', selected > 0);
      };
      control.addEventListener('change', () => {
        inputs.forEach(input => { input.checked = control.checked; });
        refresh();
      });
      inputs.forEach(input => {
        input.addEventListener('change', refresh);
        // Author styles set display:inline-flex on labels, overriding UA
        // [hidden]. Inline display avoids leaving duplicate visible choices.
        const nativeLabel = input.closest('[data-fdp-native-choice]');
        nativeLabel.hidden = true;
        nativeLabel.style.display = 'none';
      });
      group.append(label);
      group.dataset.fdpItemEnhanced = 'true';
      refresh();
    }
  }

  function init(root) {
    if (root.dataset.fdpNativeReady) return;
    root.dataset.fdpNativeReady = 'true';
    const anchor = root.dataset.fdpResultsAnchor || '';
    const productSearch = root.classList.contains('fdp-search-page');
    const links = [...root.querySelectorAll('[data-fdp-native-axis], [data-fdp-axis-link]')];
    const panels = [...root.querySelectorAll('[data-fdp-native-panel], [data-fdp-axis-panel]')];
    const axisOf = element => element.dataset.fdpNativeAxis || element.dataset.fdpAxisLink;
    const panelOf = element => element.dataset.fdpNativePanel || element.dataset.fdpAxisPanel;
    const activate = axis => {
      const target = panels.find(panel => panelOf(panel) === axis) || panels[0];
      if (!target) return;
      panels.forEach(panel => { panel.hidden = panel !== target; });
      links.forEach(link => {
        const active = axisOf(link) === panelOf(target);
        link.classList.toggle('is-active', active);
        link.setAttribute('aria-expanded', String(active));
      });
    };
    links.forEach(link => link.addEventListener('click', event => {
      event.preventDefault();
      activate(axisOf(link));
    }));
    activate(location.hash.replace('#fdp-finder-', ''));
    const form = root.querySelector('[data-fdp-native-form]');
    if (form) enhanceItemChoices(form);
    form?.querySelectorAll('[data-fdp-native-choice] input').forEach(input => {
      input.addEventListener('change', () => input.closest('[data-fdp-native-choice]').classList.toggle('is-selected', input.checked));
    });
    form?.addEventListener('submit', event => {
      event.preventDefault();
      const represented = [...form.querySelectorAll('input[name]')].map(input => input.name).filter(isNative);
      location.assign(buildUrl(location.href, {mode: 'apply', entries: [...new FormData(form)], represented, anchor, productSearch}));
    });
    root.querySelector('[data-fdp-native-clear]')?.addEventListener('click', event => {
      event.preventDefault();
      location.assign(buildUrl(location.href, {mode: 'clear', anchor, productSearch}));
    });
    root.querySelectorAll('[data-fdp-native-remove]').forEach(link => {
      // Use Shopify's removal URL (not label-based reconstruction); only clear pagination.
      const url = new URL(link.href, location.href);
      if (productSearch) url.searchParams.set('type', 'product');
      url.searchParams.delete('page');
      [...url.searchParams.keys()].filter(name => name.startsWith('fdp_')).forEach(name => url.searchParams.delete(name));
      url.hash = anchor;
      link.href = url.toString();
    });
    root.querySelectorAll('[data-fdp-sort], [data-fdp-search-sort]').forEach(sort => {
      sort.addEventListener('change', () => location.assign(buildUrl(location.href, {mode: 'sort', sort: sort.value, anchor, productSearch})));
    });
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {buildUrl, enhanceItemChoices, init};
  else {
    const mount = () => document.querySelectorAll('[data-fdp-native-surface]').forEach(init);
    mount();
    document.addEventListener('shopify:section:load', mount);
  }
})(typeof window === 'undefined' ? globalThis : window);
